import { useState } from "react";
import { Button } from "@client/components/Button";
import { hasSeen, markSeen, SEEN_EDITOR_INTRO } from "@client/lib/seenFlags";

/**
 * The first-run explainer, shown once per device above the editor.
 *
 * The editor is a wall of numbers the first time you meet it, and the three
 * things that make it legible are not discoverable by looking: that the inputs
 * are behind the rail, that it saves itself, and that the table below the charts
 * is the real answer rather than a footnote to them. Four sentences, once — then
 * it never appears again.
 *
 * It is dismissed by a button rather than a timer, and it sits in the flow
 * rather than over it: a modal on arrival is a thing to get past, not to read.
 */
export function EditorIntro({ canEdit }: { canEdit: boolean }) {
  // Read once, on mount. Re-reading on every render would make the panel vanish
  // mid-interaction if another tab dismissed it, which is a jump, not a fix.
  const [dismissed, setDismissed] = useState(() => hasSeen(SEEN_EDITOR_INTRO));

  const dismiss = () => {
    markSeen(SEEN_EDITOR_INTRO);
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <aside
      aria-labelledby="editor-intro-title"
      className="flex flex-col gap-3 rounded-xl border border-line bg-chip-bg px-4 py-3.5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 id="editor-intro-title" className="text-base font-bold text-ink">
          First time here? Three things worth knowing.
        </h2>
        <Button variant="ghost" size="sm" onClick={dismiss}>
          Got it
        </Button>
      </div>

      <ul className="flex flex-col gap-2 text-base text-ink-soft">
        <li>
          <span className="font-semibold text-ink">The rail on the left holds the inputs.</span>{" "}
          Four buttons — settings, packages, costs and revenues, adoption and churn — each opening
          what it names. Change a price or an adoption curve and the figures behind the dialog move
          as you type; none of it waits on the server, so try the number you are unsure about.
        </li>
        <li>
          <span className="font-semibold text-ink">
            {canEdit ? "It saves itself." : "You are reading, not editing."}
          </span>{" "}
          {canEdit
            ? "A couple of seconds after you stop typing, the scenario is saved and the header says so. Ctrl+S if you would rather not wait. Nothing is lost if the tab closes."
            : "This scenario was shared with you to look at. Duplicate it and the copy is yours to change, with the original left exactly as it is."}
        </li>
        <li>
          <span className="font-semibold text-ink">
            The charts are a summary; the table is the answer.
          </span>{" "}
          &ldquo;Year by year&rdquo;, below the charts, carries every figure the projection produced
          — and it is what the Export button writes out.
        </li>
      </ul>
    </aside>
  );
}
